import React from 'react';
import OpenSeadragon from 'openseadragon';
import './SVSViewer.css';
import html2canvas from 'html2canvas';

class SVSViewer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      viewer: null,
      isLoading: true,
      error: null,
      zoomLevel: 1,
      mousePosition: { x: 0, y: 0 },
      annotations: props.initialAnnotations || [],
      screenshotPreview: null,
      controlsVisible: true
    };
    this.viewerRef = React.createRef();
  }

  componentDidMount() {
    this.initOpenSeadragon();
    this.addResizeListener();
  }

  componentDidUpdate(prevProps) {
    // 当 DZI URL 改变时重新加载查看器
    if (prevProps.dziUrl !== this.props.dziUrl) {
      this.resetViewer();
    }

    // 当初始标注改变时更新
    if (prevProps.initialAnnotations !== this.props.initialAnnotations) {
      this.setState(
        { annotations: this.props.initialAnnotations || [] },
        () => {
          this.addAnnotationsToViewer();
        }
      );
    }
  }

  componentWillUnmount() {
    this.removeResizeListener();
    if (this.state.viewer) {
      this.state.viewer.destroy();
    }
  }

  addResizeListener = () => {
    window.addEventListener('resize', this.handleResize);
  };

  removeResizeListener = () => {
    window.removeEventListener('resize', this.handleResize);
  };

  takeScreenshot = () => {
    const viewerContainer = this.viewerRef.current;

    if (!viewerContainer) {
      console.error("无法找到查看器容器");
      return;
    }

    // 使用 html2canvas 截取 SVSViewer 内容
    html2canvas(viewerContainer, {
      useCORS: true,  // 启用跨域支持
      allowTaint: false,
      scale: 2  // 提高截图分辨率
    }).then(canvas => {
      // 将 canvas 转换为 Base64 图像数据
      const imgBase64 = canvas.toDataURL('image/png');

      // 更新本地状态
      this.setState({ screenshotPreview: imgBase64 });

      // 触发父组件的回调
      if (this.props.onScreenshot) {
        this.props.onScreenshot(imgBase64);
      } else {
        // 如果没有提供回调，则直接下载图片
        const link = document.createElement('a');
        link.href = imgBase64;
        link.download = `svs-screenshot-${Date.now()}.png`;
        link.click();
      }
    }).catch(error => {
      console.error("截图失败:", error);
      this.setState({
        error: "截图失败，请重试"
      });
      message.error("截图失败，请重试");
    });
  };

  handleResize = () => {
    if (this.state.viewer) {
      setTimeout(() => {
        this.state.viewer.viewport.resize();
      }, 300);
    }
  };

  initOpenSeadragon = () => {
    const { dziUrl } = this.props;

    try {
      // 清除任何之前的错误状态
      this.setState({ isLoading: true, error: null });

      const viewer = OpenSeadragon({
        element: this.viewerRef.current,
        prefixUrl: 'https://test.xytechsz.com/openseadragon-images/',
        tileSources: dziUrl,
        showNavigationControl: this.state.controlsVisible,
        showNavigator: true,
        navigatorPosition: 'TOP_RIGHT',
        gestureSettingsMouse: {
          clickToZoom: false,
          dblClickToZoom: true
        },
        errorHandler: this.handleOpenSeadragonError
      });

      // 添加事件监听器
      viewer.addHandler('open', this.handleOpenSuccess);
      viewer.addHandler('open-failed', this.handleOpenFailed);
      viewer.addHandler('animation', this.handleAnimation);
      viewer.addHandler('canvas-mousemove', this.handleMouseMove);
      viewer.addHandler('canvas-click', this.handleCanvasClick);

      // 保存查看器实例到状态
      this.setState({ viewer }, () => {
        // 添加初始标注
        this.addAnnotationsToViewer();
      });
    } catch (error) {
      this.setState({
        isLoading: false,
        error: `初始化失败: ${error.message}`
      });
      console.error('OpenSeadragon 初始化错误:', error);
    }
  };

  resetViewer = () => {
    if (this.state.viewer) {
      this.state.viewer.destroy();
      this.setState({ viewer: null, isLoading: true }, () => {
        this.initOpenSeadragon();
      });
    } else {
      this.initOpenSeadragon();
    }
  };

  handleOpenSuccess = () => {
    const { viewer } = this.state;

    if (viewer) {
      // 方案1：直接居中
      viewer.viewport.goHome(true); // true 表示立即执行

      // 或者方案2：获取图片尺寸后计算居中位置
      const bounds = viewer.world.getItemAt(0).getBounds();
      const center = bounds.getCenter();
      viewer.viewport.panTo(center, true);
      this.setState({ isLoading: false });
    }
  };

  handleOpenFailed = (event) => {
    this.setState({
      isLoading: false,
      error: `无法加载切片源: ${event.message}`
    });
    console.error('打开切片源失败:', event);
  };

  handleOpenSeadragonError = (error) => {
    this.setState({
      isLoading: false,
      error: `OpenSeadragon 错误: ${error.message}`
    });
    console.error('OpenSeadragon 错误:', error);
  };

  handleAnimation = () => {
    if (this.state.viewer) {
      this.setState({ zoomLevel: this.state.viewer.viewport.getZoom() });
    }
  };

  handleMouseMove = (event) => {
    if (!this.state.viewer) return;

    const position = this.convertToImageCoordinates(event.position);
    this.setState({ mousePosition: position });
  };

  handleCanvasClick = (event) => {
    if (this.state.isLoading) return;

    const position = this.convertToImageCoordinates(event.position);
    if (this.props.onCanvasClick) {
      this.props.onCanvasClick(position);
    }
  };

  convertToImageCoordinates = (point) => {
    const { viewer } = this.state;
    if (!viewer) return { x: 0, y: 0 };

    const webPoint = point;
    const viewportPoint = viewer.viewport.pointFromPixel(webPoint);
    return viewer.viewport.viewportToImageCoordinates(viewportPoint);
  };

  addAnnotationsToViewer = () => {
    const { viewer, annotations } = this.state;
    if (!viewer || !annotations || annotations.length === 0) return;

    // 确保查看器已完全打开
    if (viewer.isOpen()) {
      this.renderAnnotations();
    } else {
      viewer.addOnceHandler('open', () => {
        this.renderAnnotations();
      });
    }
  };

  renderAnnotations = () => {
  const { viewer, annotations } = this.state;
  if (!viewer || !annotations || annotations.length === 0) return;

  const viewport = viewer.viewport;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const containerSize = viewer.containerSize;
  if (!containerSize || typeof containerSize.x === 'undefined') {
    console.warn("查看器容器尺寸无效");
    return;
  }

  canvas.width = containerSize.x;
  canvas.height = containerSize.y;

  context.clearRect(0, 0, canvas.width, canvas.height);

  annotations.forEach(annotation => {
    const rect = viewport.imageToViewportRectangle(
      new OpenSeadragon.Rect(annotation.x, annotation.y, annotation.width, annotation.height)
    );
    const screenRect = viewport.viewportToWindowCoordinates(rect);

    context.strokeStyle = annotation.stroke || 'red';
    context.lineWidth = 2;
    context.fillStyle = annotation.color || 'rgba(255, 0, 0, 0.3)';
    context.fillRect(screenRect.x, screenRect.y, screenRect.width, screenRect.height);
    context.strokeRect(screenRect.x, screenRect.y, screenRect.width, screenRect.height);
  });

  let overlay = document.getElementById('annotation-canvas');
  if (!overlay) {
    overlay = document.createElement('canvas');
    overlay.id = 'annotation-canvas';
    overlay.style.position = 'absolute';
    overlay.style.left = '0px';
    overlay.style.top = '0px';
    overlay.style.zIndex = '999';

    try {
      viewer.canvas.appendChild(overlay);
    } catch (e) {
      console.error("无法将注解层添加到查看器", e);
      return;
    }
  }

  const overlayContext = overlay.getContext('2d');
  overlayContext.clearRect(0, 0, overlay.width, overlay.height);
  overlay.width = canvas.width;
  overlay.height = canvas.height;
  overlayContext.drawImage(canvas, 0, 0);
};

  toggleControls = () => {
    this.setState(
      prevState => ({ controlsVisible: !prevState.controlsVisible }),
      () => {
        this.resetViewer();
      }
    );
  };

  reloadViewer = () => {
    this.resetViewer();
  };

  render() {
    const { isLoading, error, zoomLevel, mousePosition, controlsVisible, screenshotPreview } = this.state;
    const { height = '60vh' } = this.props;

    return (
      <div className="svs-viewer-container" style={{ height }}>
        {/* 加载提示 */}
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>加载切片源...</p>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="error-overlay">
            <div className="error-content">
              <h3>加载错误</h3>
              <p>{error}</p>
              <p>尝试访问的路径: {this.props.dziUrl}</p>

              <div className="error-actions">
                <button onClick={this.reloadViewer}>重试</button>
                <button onClick={this.toggleControls}>
                  {controlsVisible ? '隐藏控制' : '显示控制'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 查看器容器 */}
        <div
          ref={this.viewerRef}
          className="viewer-element"
          style={{ width: '100%', height: '100%' }}
        />

        {/* 控制面板和截图按钮 */}
        <div className="position-info">
          <span>缩放: {zoomLevel.toFixed(2)}x</span>

          {/* 添加截图按钮 */}
          <button
            className="screenshot-button"
            onClick={this.takeScreenshot}
            title="截图当前视图"
          >
            📸 截图
          </button>
        </div>

        {/* 截图预览 */}
        {screenshotPreview && (
          <div className="screenshot-preview">
            <img src={screenshotPreview} alt="截图预览" />
          </div>
        )}
      </div>
    );
  }
}

export default SVSViewer;